import { useContext } from 'react';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import { Button, Drawer } from '@arkestro/arkestro-design-system';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { ID } from 'types';
import { UserContext } from '@providers/UserProvider/UserProvider';
import { useGetOverrideAttribution } from '@features/suggested-price-column/api/get-override-attribution';
import { useGetSuggestedPriceExplanation } from '@features/suggested-price-column/api/get-suggested-price-explanation';
import { SuggestedPriceFactorGroups } from '@features/suggested-price-column/components/suggested-price-factor-groups/suggested-price-factor-groups';
import { SuggestedPriceNotice } from '@features/suggested-price-column/components/suggested-price-notice/suggested-price-notice';
import { SuggestedPriceOverrideImpact } from '@features/suggested-price-column/components/suggested-price-override-impact/suggested-price-override-impact';
import { SUGGESTED_PRICE_CALCULATIONS } from '@features/suggested-price-column/components/suggested-price-stepper/suggested-price-calculations';
import { SuggestedPriceStepper } from '@features/suggested-price-column/components/suggested-price-stepper/suggested-price-stepper';
import { discountPercentFromBaseline } from '@features/suggested-price-column/utils/discount-percent';
import { formatOverriddenAt } from '@features/suggested-price-column/utils/format-overridden-at';
import { resolveOverrideAttribution } from '@features/suggested-price-column/utils/resolve-override-attribution';

import styles from './suggested-price-info-drawer.module.scss';

const COPY_SCOPE = 'bid_request.line_items.suggested_price.explanation';
const DRAWER_WIDTH = 560;

interface SuggestedPriceInfoDrawerProps {
  aiValue: number | null;
  currency: string;
  /** Event the line item belongs to — both reads below are scoped to it. */
  eventId: ID;
  hasOverride: boolean;
  itemName: string;
  lineItemId: ID;
  lotId: ID;
  /** The row's Negotiation Starting Price, per unit — the figure both discounts are taken against. */
  negotiationStartingPrice: number | null;
  onClose: () => void;
  onReset: () => void;
  open: boolean;
  overrideValue: number | null;
  pricePrecision: number;
}

/**
 * The "Why this price?" drawer for a Suggested Price cell.
 *
 * Both variants open on the same Negotiation Starting Price -> AI -> final stepper. The second section diverges: an
 * unoverridden row explains the model's reasoning from the /v3/explain factors, while an overridden
 * row reports the impact of the buyer's own change and offers a reset.
 *
 * An unoverridden row resolves to one of four states, in order: in-flight, failed,
 * nothing-to-report, or the factor list. Nothing-to-report is a successful call whose factors all
 * fell below the model's cutoff, so it reads as information rather than an error.
 *
 * An overridden row's "Overridden by ..." sentence needs both an editor and a timestamp and is
 * omitted when either is missing, since an override with no audit row behind it is normal. The
 * reset link is independent of that sentence, and shows only when there is an AI price to fall
 * back to.
 *
 * It issues its own reads, gated on `open`. Only one of these is mounted per grid rather than one
 * per cell (see SuggestedPriceInfoDrawerHost), so that gate is what keeps a grid of rows from each
 * holding a query.
 */
export const SuggestedPriceInfoDrawer = ({
  aiValue,
  currency,
  eventId,
  hasOverride,
  itemName,
  lineItemId,
  lotId,
  negotiationStartingPrice,
  onClose,
  onReset,
  open,
  overrideValue,
  pricePrecision,
}: SuggestedPriceInfoDrawerProps) => {
  const { t } = useTranslation('common');
  const { date_part_order: datePartOrder } = useContext(UserContext);
  const {
    data: explanation,
    isError,
    isFetching: isLoading,
  } = useGetSuggestedPriceExplanation({
    enabled: open && !hasOverride,
    eventId,
    lineItemId,
    lotId,
  });
  const factors = explanation?.factors ?? [];

  const { data: overrideAttribution } = useGetOverrideAttribution({
    bidRequestId: eventId,
    enabled: open && hasOverride,
    lineItemId,
    overrideValue,
  });

  const aiDiscount = discountPercentFromBaseline(aiValue, negotiationStartingPrice);
  const overrideDiscount = discountPercentFromBaseline(overrideValue, negotiationStartingPrice);
  const attribution = resolveOverrideAttribution(
    overrideAttribution ?? {},
    isoTimestamp => formatOverriddenAt(isoTimestamp, datePartOrder),
  );

  const renderExplanation = () => {
    if (isLoading) {
      return (
        <div className={styles.loading} data-testid='suggested-price-drawer-loading'>
          <Loading3QuartersOutlined spin />
        </div>
      );
    }

    if (isError) {
      return (
        <SuggestedPriceNotice copyKey='load_failure' dataTestId='suggested-price-drawer-failure' tone='error' />
      );
    }

    if (factors.length === 0) {
      return (
        <SuggestedPriceNotice copyKey='empty' dataTestId='suggested-price-drawer-empty' tone='info' />
      );
    }

    return <SuggestedPriceFactorGroups currency={currency} factors={factors} pricePrecision={pricePrecision} />;
  };

  return (
    <Drawer
      classNames={{ body: styles.body, header: styles.header }}
      dataTestId='suggested-price-info-drawer'
      onClose={onClose}
      open={open}
      title={(
        <span
          className={styles.title}
          title={t(`${COPY_SCOPE}.title`, { itemName })}
        >
          {t(`${COPY_SCOPE}.title`, { itemName })}
        </span>
      )}
      width={DRAWER_WIDTH}
    >
      <section className={styles.section}>
        <h3 className={styles.heading}>{t(`${COPY_SCOPE}.calculated_heading`)}</h3>
        <p className={styles.intro}>{t(`${COPY_SCOPE}.calculated_intro`)}</p>
        <SuggestedPriceStepper
          amounts={{ aiValue, negotiationStartingPrice, overrideValue }}
          currency={currency}
          percents={{ aiDiscount, overrideDiscount }}
          pricePrecision={pricePrecision}
          steps={SUGGESTED_PRICE_CALCULATIONS[hasOverride ? 'overridden' : 'ai_generated']}
        />
        {hasOverride ? (
          <p className={styles.footnote} data-testid='suggested-price-drawer-attribution'>
            {attribution != null && (
              <span className={styles.overriddenBy} data-testid='suggested-price-drawer-overridden-by'>
                <Trans
                  components={{ strong: <strong /> }}
                  i18nKey={`common:${COPY_SCOPE}.overridden_by`}
                  values={{ date: attribution.date, name: attribution.name }}
                />
              </span>
            )}
            {aiValue != null && (
              <Button
                className={styles.inlineLink}
                dataTestId='suggested-price-drawer-reset'
                onClick={onReset}
                type='link'
              >
                {t(`${COPY_SCOPE}.reset`)}
              </Button>
            )}
          </p>
        ) : (
          <p className={styles.footnote}>{t(`${COPY_SCOPE}.override_hint`)}</p>
        )}
      </section>

      <section className={styles.sectionLast}>
        <h3 className={styles.heading}>
          {hasOverride ? t(`${COPY_SCOPE}.impact_heading`) : t(`${COPY_SCOPE}.drove_heading`)}
        </h3>
        <p className={styles.intro}>
          {hasOverride ? t(`${COPY_SCOPE}.impact_intro`) : t(`${COPY_SCOPE}.drove_intro`)}
        </p>
        {hasOverride ? (
          <SuggestedPriceOverrideImpact
            aiValue={aiValue}
            currency={currency}
            negotiationStartingPrice={negotiationStartingPrice}
            overrideValue={overrideValue}
            pricePrecision={pricePrecision}
          />
        ) : renderExplanation()}
      </section>
    </Drawer>
  );
};
